/**
 * SYND-18 — marketplace first: how a row that holds both sources is composed.
 *
 * PO decision D2 (2026-10-13) puts indexed rows in "New this week", composed
 * by this rule:
 *
 *   1. The marketplace block comes first, newest first, at most
 *      NEW_THIS_WEEK_MARKETPLACE_MAX (5) cards.
 *   2. The indexed block comes second, newest first (indexed_at desc, id desc),
 *      filling the row to NEW_THIS_WEEK_ROW_MAX (8) cards in total, with at
 *      most NEW_THIS_WEEK_PER_SOURCE_MAX (2) from any one source.
 *   3. No indexed card ever precedes or displaces a marketplace card.
 *
 * WHY THE TWO BLOCKS NEVER INTERLEAVE
 *   A marketplace card is a listing an Afriqahome agent published, with a
 *   contact route behind it. An indexed card is a fact we found on someone
 *   else's public page, with no contact route and never a verified badge.
 *   Sorting the two into one list by date would let a batch ingest — which
 *   stamps a whole source in one moment — push every agent listing of the week
 *   out of the row. So the order between the blocks is fixed by kind, and date
 *   orders cards only inside a block. The indexed block fills the slots the
 *   marketplace block leaves empty, never the ones it uses.
 *
 * ROWS IN THE WRONG LIST ARE DROPPED, NOT MOVED. The head takes only
 * `source === 'listing'` rows and the tail only `source === 'syndicated'` ones,
 * so a caller that hands an indexed row in as marketplace cannot put it in
 * front. That makes rule 3 structural rather than a convention every caller has
 * to remember.
 *
 * THE PER-SOURCE CAP applies to indexed rows only, keyed by `sourceKey`
 * (`source_slug`). Marketplace rows are never capped by agent or agency: a
 * week in which one agency lists five homes shows five homes. The view behind
 * the indexed half already caps each source at two per property type
 * (migration 20261013100000); the cap here is the defence in depth that keeps
 * an unfiltered row at two per source overall.
 *
 * Pure on purpose: no React and no import that reaches lib/api/client.ts, so
 * pages/__tests__/marketplaceFirst.test.ts can run it under plain `npx tsx`.
 * NewThisWeekRow.tsx cannot be imported that way (its ListingCard import
 * reaches client.ts through UsdApprox and CurrencyContext), which is why the
 * window selector moved here from that file.
 *
 * SYND-20 reuses `composeMarketplaceFirst` with its own limits for the
 * "More homes for sale in {area}" rail.
 */

import { NEW_THIS_WEEK_DAYS, daysSince, type HomeListingView } from './listingView';

/** Marketplace cards at the head of "New this week". */
export const NEW_THIS_WEEK_MARKETPLACE_MAX = 5;
/** Cards in the whole row, marketplace and indexed together. */
export const NEW_THIS_WEEK_ROW_MAX = 8;
/** Indexed cards from any one source. */
export const NEW_THIS_WEEK_PER_SOURCE_MAX = 2;

export interface MarketplaceFirstLimits {
  /** Most marketplace cards the head may hold. */
  marketplaceMax: number;
  /** Most cards in the whole row. */
  rowMax: number;
  /** Most indexed cards from one source (`sourceKey`). */
  perSourceMax: number;
}

/** Epoch ms of `datedAt`, or null when there is none or it does not parse. */
function stampOf(listing: Pick<HomeListingView, 'datedAt'>): number | null {
  if (!listing.datedAt) return null;
  const stamp = Date.parse(listing.datedAt);
  return Number.isNaN(stamp) ? null : stamp;
}

/**
 * Newest first, ties broken by id descending — the client half of
 * `order=indexed_at.desc,id.desc`. A batch ingest stamps a whole source with
 * one timestamp, so without the id the order inside a tie would depend on the
 * order the rows arrived in. Rows without a usable date sort last.
 */
export function newestFirst(
  a: Pick<HomeListingView, 'datedAt' | 'id'>,
  b: Pick<HomeListingView, 'datedAt' | 'id'>
): number {
  const sa = stampOf(a);
  const sb = stampOf(b);
  if (sa !== sb) {
    if (sa === null) return 1;
    if (sb === null) return -1;
    return sb - sa;
  }
  if (a.id === b.id) return 0;
  return a.id < b.id ? 1 : -1;
}

/**
 * Inside the "New this week" window: dated, and at most NEW_THIS_WEEK_DAYS
 * whole days old by `daysSince` (which floors, so this admits anything under
 * 8 × 24 h). Moved here from NewThisWeekRow.tsx; the same rule as before.
 *
 * For indexed rows the view is the STRICTER bound when the rows are fetched:
 * `syndicated_new_this_week` returns only rows indexed in the last 7 × 24 h.
 * The SPA fetches them on mount and on a chip change, not again, so in a tab
 * left open it is this client bound (under 8 × 24 h) that finally drops an
 * indexed row. Both bounds are kept as they are on purpose (Brain ruling 5,
 * 2026-10-13).
 */
export function isNewThisWeek(
  listing: Pick<HomeListingView, 'datedAt'>,
  now: number = Date.now()
): boolean {
  const days = daysSince(listing.datedAt, now);
  return days !== null && days <= NEW_THIS_WEEK_DAYS;
}

/** Listings inside the freshness window, newest first (ties by id). */
export function selectNewThisWeek(
  listings: readonly HomeListingView[],
  limit: number = Number.POSITIVE_INFINITY,
  now: number = Date.now()
): HomeListingView[] {
  return listings
    .filter((listing) => isNewThisWeek(listing, now))
    .sort(newestFirst)
    .slice(0, limit);
}

/**
 * The composition: the marketplace block, then the indexed block, never
 * interleaved. See the header for each rule. Inputs are not mutated; each
 * block is sorted newest first here, so callers need not pre-sort.
 */
export function composeMarketplaceFirst(
  marketplace: readonly HomeListingView[],
  indexed: readonly HomeListingView[],
  limits: MarketplaceFirstLimits
): HomeListingView[] {
  const rowMax = Math.max(0, limits.rowMax);
  const seen = new Set<string>();
  const firstSighting = (listing: HomeListingView) => {
    const key = `${listing.source}:${listing.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  };

  const head = marketplace
    .filter((listing) => listing.source === 'listing')
    .slice()
    .sort(newestFirst)
    .filter(firstSighting)
    .slice(0, Math.min(Math.max(0, limits.marketplaceMax), rowMax));

  const tail: HomeListingView[] = [];
  const perSource = new Map<string, number>();
  const candidates = indexed
    .filter((listing) => listing.source === 'syndicated')
    .slice()
    .sort(newestFirst);

  for (const listing of candidates) {
    if (head.length + tail.length >= rowMax) break;
    if (!firstSighting(listing)) continue;
    // A missing key groups with every other missing key rather than escaping
    // the cap. Every indexed row carries one (source_slug is NOT NULL); this
    // only decides what a bug would cost, and the answer is fewer cards.
    const source = listing.sourceKey ?? '';
    const used = perSource.get(source) ?? 0;
    if (used >= limits.perSourceMax) continue;
    perSource.set(source, used + 1);
    tail.push(listing);
  }

  return [...head, ...tail];
}

/**
 * "New this week" as rendered by both trees: each block windowed, then
 * composed by the rule in the header. Empty when both blocks are empty, which is the one case the
 * row collapses.
 */
export function composeNewThisWeek(
  marketplace: readonly HomeListingView[],
  indexed: readonly HomeListingView[],
  now: number = Date.now()
): HomeListingView[] {
  return composeMarketplaceFirst(
    selectNewThisWeek(marketplace, Number.POSITIVE_INFINITY, now),
    selectNewThisWeek(indexed, Number.POSITIVE_INFINITY, now),
    {
      marketplaceMax: NEW_THIS_WEEK_MARKETPLACE_MAX,
      rowMax: NEW_THIS_WEEK_ROW_MAX,
      perSourceMax: NEW_THIS_WEEK_PER_SOURCE_MAX,
    }
  );
}
